import classes from "./ListingsPage.module.scss";
import { FullPageContainer } from "../../common/FullPageContainer/FullPageContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import { IListing } from "../../interfaces";
import { useSelector, useDispatch } from "react-redux";
import { ExternalLinkActive } from "../../../assets/icons/ExternalLinkActive";
import { ExternalLinkInactive } from "../../../assets/icons/ExternalLinkInactive";
import { AirbnbIcon } from "../../../assets/icons/AirbnbIcon";
import { VrboIcon } from "../../../assets/icons/VrboIcon";
import { ExpediaIcon } from "../../../assets/icons/ExpediaIcon";
import { BookingIcon } from "../../../assets/icons/BookingIcon";
import { GoogleIcon } from "../../../assets/icons/GoogleIcon";
import { AIRBNB, BOOKING, EXPEDIA, VRBO, GOOGLE } from "../../../utils/constants/commonConstants";
import {isMobileModeSelector} from "../../../redux/selectors/appStatusSelector";
import { listingsDataSelector } from "../../../redux/selectors/listingsDataSelector";

import {
  GET_LISTINGS_DATA_REQUEST
} from '../../../redux/actions';

import { listingsSelector } from '@/redux/selectors/listingsSelector';
import { useMemo, useEffect } from 'react';

const channelToIconMap: any = {
  AIRBNB: {
    icon: <AirbnbIcon />,
    name: "AirBnb",
  },
  BOOKINGCOM: {
    icon: <BookingIcon />,
    name: "Booking",
  },
  EXPEDIA: {
    icon: <ExpediaIcon />,
    name: "Expedia",
  },
  VRBO: {
    icon: <VrboIcon />,
    name: "Vrbo",
  },
  GOOGLEVACATIONRENTALS: {
    icon: <GoogleIcon />,
    name: "Google",
  },
};

export const ListingsPage = () => {
  const dispatch = useDispatch();
  const isMobileView = useSelector(isMobileModeSelector);

   const listings: IListing[] = useSelector(listingsSelector);
  const body = useMemo(() => {
    return (listings !== null ? listings : [])
      .filter((listing) => listing.is_listed)
      .map((listing) => listing.property_id);
  }, [listings]);

  const processedListings = useSelector(listingsDataSelector('last 30 days'))?.summary;

  useEffect(() => {    
    dispatch({
      type: GET_LISTINGS_DATA_REQUEST,
      payload: body,
    });
  }, [dispatch, body]);

  return (
    <FullPageContainer className={classes.listingsWrapper}>
      <div className={classes.listingsTableContainer}>
        <h4>My Listings</h4>
        <Table aria-label="Listings table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align={isMobileView ? "center" : "left"}>Listing</TableCell>
              {isMobileView ? (
                  <TableCell align="center" colSpan={2}>
                    Channel Link
                  </TableCell>
                ) : (
                  <>
                    <TableCell align="left">Channel</TableCell>
                    <TableCell align="left">Link</TableCell>
                  </>
              )}
              <TableCell align={isMobileView ? "center" : "left"}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {processedListings &&
              processedListings.map(
              (
                item: {
                  listing_id: string;
                  nickname: string;
                  channel_name: string;
                  channel_link: string;
                  is_listed: number; // assuming it's 1 or 0 from API
                },
                index: number
              ) => {
                const { is_listed, channel_name, channel_link, listing_id, nickname } = item;

                return (
                  <TableRow className={classes.tableRow} key={`${listing_id}_${index}`}>
                    <TableCell align={isMobileView ? "center" : "left"}>
                      <span className={classes.channelCell}>                        
                        {nickname}
                      </span>
                    </TableCell>
                    {isMobileView ? (
                      <TableCell align="center" colSpan={2}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px", // consistent gap between all elements
                            maxWidth:"133px",
                            marginLeft:'auto',
                            marginRight:'auto',
                          }}
                        >
                          {is_listed ? (
                            <a
                              className={classes.listingLink}
                              href={channel_link}
                              rel="noreferrer"
                              target="_blank"
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <ExternalLinkActive />
                            </a>
                          ) : (
                            <span
                              className={`${classes.listingLink} ${classes.inactive}`}
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <ExternalLinkInactive />
                            </span>
                          )}

                          <span
                            style={{
                              width: "30px",
                              height: "30px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {channelToIconMap[channel_name.toUpperCase()]?.icon}
                          </span>

                          <span style={{ fontSize: "14px", fontWeight: "500" }}>
                            {channelToIconMap[channel_name.toUpperCase()]?.name ?? channel_name}
                          </span>
                        </span>
                      </TableCell>
                      ) : (
                      <>
                        <TableCell align="left">
                          <span className={classes.channelCell}>
                            {channelToIconMap[channel_name.toUpperCase()]?.icon}
                            {channelToIconMap[channel_name.toUpperCase()]?.name ?? channel_name}
                          </span>
                        </TableCell>
                        <TableCell align="left">
                          {is_listed ? (
                            <a
                              className={classes.listingLink}
                              href={channel_link}
                              rel="noreferrer"
                              target="_blank"
                            >
                              View Listing
                              <ExternalLinkActive />
                            </a>
                          ) : (
                            <span className={`${classes.listingLink} ${classes.inactive}`}>
                              View Listing
                              <ExternalLinkInactive />
                            </span>
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell align={isMobileView ? "center" : "left"}>
                      {is_listed ? (
                        <div className={classes.activeBadge}>
                          {isMobileView ? "" : "Listed"}<span></span>
                        </div>
                      ) : (
                        <div className={classes.inactiveBadge}>
                          {isMobileView ? "" : "Not Listed"}<span></span>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              }
            )}
          </TableBody>
        </Table>
      </div>
    </FullPageContainer>
  );
};
